import { useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  getFileIcon,
  getDocumentMime,
  getDocumentSourceType,
  resolveDocumentUrl,
  resolveDocumentFilename,
  resolveDocumentDisplayName,
  type DocumentSourceType,
} from '@/lib/document-utils'
import { orpc } from '@/lib/orpc-client'

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface PickedDocument {
  id: string
  title: string
  slug: string
  /** Display name shown in UI — title for editor docs, source filename for uploads */
  displayName: string
  /** Filename with correct extension for agent processing (e.g. "report.pdf", "notes.md") */
  filename: string
  /** Workspace-relative URL for the file */
  url: string
  /** MIME type */
  contentType: string
  /** Human-readable source category */
  sourceType: DocumentSourceType
}

export interface DocumentPickerProps {
  projectId: string
  /** IDs already attached — shown checked & locked */
  selectedIds?: string[]
  /** Called when user confirms selection */
  onSelect: (documents: PickedDocument[]) => void
  /** Cap on total selectable documents */
  maxSelections?: number
}

/* ------------------------------------------------------------------ */
/* Internal types mirroring the API shape                              */
/* ------------------------------------------------------------------ */

interface DocItem {
  id: string
  title: string
  slug: string
  tags: Array<{ id: string; name: string; color: string }>
  sourceFilePath?: string | null
  conversionStatus?: 'pending' | 'converting' | 'completed' | 'failed' | null
}

interface TagItem {
  id: string
  name: string
  color: string
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export function DocumentPicker({
  projectId,
  selectedIds = [],
  onSelect,
  maxSelections,
}: DocumentPickerProps) {
  const { t } = useTranslation('chat')
  const [documents, setDocuments] = useState<DocItem[]>([])
  const [tags, setTags] = useState<TagItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFiltering, setIsFiltering] = useState(false)
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [activeTagId, setActiveTagId] = useState<string | null>(null)

  const lockedIds = new Set(selectedIds)

  /* ---- data fetching ---- */

  const fetchDocuments = useCallback(
    async (tagId: string | null) => {
      try {
        const params: { projectId: string; tagId?: string } = { projectId }
        if (tagId) params.tagId = tagId
        const data = await orpc.documents.list(params)
        setDocuments(data.items as DocItem[])
      } catch {
        /* ignore */
      }
    },
    [projectId]
  )

  const fetchTags = useCallback(async () => {
    try {
      const data = await orpc.documentTags.list({ projectId })
      setTags(data.items as TagItem[])
    } catch {
      /* ignore */
    }
  }, [projectId])

  // Initial load
  useEffect(() => {
    setIsLoading(true)
    Promise.all([fetchDocuments(null), fetchTags()]).finally(() => setIsLoading(false))
  }, [fetchDocuments, fetchTags])

  // Re-fetch when tag filter changes (skip initial null → null)
  const handleTagChange = useCallback(
    async (tagId: string | null) => {
      setActiveTagId(tagId)
      setIsFiltering(true)
      await fetchDocuments(tagId)
      setIsFiltering(false)
    },
    [fetchDocuments]
  )

  /* ---- selection logic ---- */

  const newCheckedCount = [...checkedIds].filter((id) => !lockedIds.has(id)).length
  const totalChecked = checkedIds.size
  const atLimit = maxSelections != null && totalChecked >= maxSelections

  const toggleCheck = (id: string) => {
    if (lockedIds.has(id)) return
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        if (maxSelections != null && next.size >= maxSelections) return prev
        next.add(id)
      }
      return next
    })
  }

  const handleConfirm = () => {
    const picked: PickedDocument[] = []
    for (const id of checkedIds) {
      if (lockedIds.has(id)) continue
      const doc = documents.find((d) => d.id === id)
      if (!doc) continue
      picked.push({
        id: doc.id,
        title: doc.title,
        slug: doc.slug,
        displayName: resolveDocumentDisplayName(doc),
        filename: resolveDocumentFilename(doc),
        url: resolveDocumentUrl(projectId, doc),
        contentType: getDocumentMime(doc.sourceFilePath),
        sourceType: getDocumentSourceType(doc.sourceFilePath),
      })
    }
    onSelect(picked)
  }

  /* ---- helpers ---- */

  const isConverting = (status: DocItem['conversionStatus']) =>
    status === 'pending' || status === 'converting'

  /* ---- render ---- */

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <Command shouldFilter={true} className="rounded-none border-0">
      <CommandInput placeholder={t('documentPicker.searchPlaceholder')} />

      {/* Tag filter row */}
      {tags.length > 0 && (
        <div className="flex gap-1.5 px-3 py-1.5 border-b border-border overflow-x-auto flex-nowrap scrollbar-hide">
          <button
            type="button"
            onClick={() => void handleTagChange(null)}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap transition-colors',
              activeTagId === null
                ? 'bg-accent text-accent-foreground font-medium'
                : 'text-muted-foreground hover:bg-accent/50'
            )}
          >
            {t('documentPicker.all')}
          </button>
          {tags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => void handleTagChange(tag.id)}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap transition-colors',
                activeTagId === tag.id
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/50'
              )}
            >
              <span
                className="h-2.5 w-2.5 rounded-full inline-block flex-shrink-0"
                style={{ backgroundColor: tag.color }}
              />
              {tag.name}
            </button>
          ))}
        </div>
      )}

      <CommandList
        className={cn('max-h-[240px] transition-opacity duration-150', isFiltering && 'opacity-50')}
      >
        <CommandEmpty>{t('documentPicker.empty')}</CommandEmpty>
        <CommandGroup>
          {documents.map((doc) => {
            const Icon = getFileIcon(doc.sourceFilePath)
            const converting = isConverting(doc.conversionStatus)
            const checked = checkedIds.has(doc.id) || lockedIds.has(doc.id)
            const disabled = converting || (!checked && atLimit)

            return (
              <CommandItem
                key={doc.id}
                value={doc.title}
                disabled={disabled}
                onSelect={() => toggleCheck(doc.id)}
                className="gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  disabled={disabled || lockedIds.has(doc.id)}
                  tabIndex={-1}
                  className="pointer-events-none"
                />
                <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{doc.title}</span>

                {/* tag dots */}
                {doc.tags.length > 0 && (
                  <span className="flex gap-0.5 flex-shrink-0">
                    {doc.tags.map((t) => (
                      <span
                        key={t.id}
                        className="h-2 w-2 rounded-full inline-block"
                        style={{ backgroundColor: t.color }}
                        title={t.name}
                      />
                    ))}
                  </span>
                )}

                {converting && (
                  <Badge variant="secondary" className="gap-1 text-xs px-1.5 py-0 flex-shrink-0">
                    <Loader2 className="h-3 w-3 animate-spin" />
                  </Badge>
                )}
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-3 py-2">
        <span className="text-xs text-muted-foreground">
          {newCheckedCount > 0
            ? t('documentPicker.selected', { count: newCheckedCount })
            : t('documentPicker.noneSelected')}
        </span>
        <Button size="sm" disabled={newCheckedCount === 0} onClick={handleConfirm}>
          {t('documentPicker.add')}
        </Button>
      </div>
    </Command>
  )
}
